import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as api from "../../api/index";
import { IoMdArrowBack } from "react-icons/io";
import { FiPhone, FiMapPin, FiCamera, FiImage, FiCheckCircle, FiChevronRight } from "react-icons/fi";

const lagosLGAs = [
  "Agege", "Ajeromi-Ifelodun", "Alimosho", "Amuwo-Odofin", "Apapa", "Badagry",
  "Epe", "Eti-Osa", "Ibeju-Lekki", "Ifako-Ijaiye", "Ikeja", "Ikorodu", "Kosofe",
  "Lagos Island", "Lagos Mainland", "Mushin", "Ojo", "Oshodi-Isolo",
  "Shomolu", "Surulere",
];

const CompleteRiderPage = ({ refreshAuth }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state?.email || "";

  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [lga, setLga] = useState("");

  const [selfieFile, setSelfieFile] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!address || !phone || !lga || !selfieFile || !companyLogo) {
      setMessage("⚠ Please fill all fields and upload required files.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("address", address);
      formData.append("phone", phone);
      formData.append("lga", lga);
      formData.append("selfieFile", selfieFile);
      formData.append("companyLogo", companyLogo);

      await api.CompleteRiderProfile(formData);

      navigate("/confirm-email", {
        state: { email: email },
      });
    } catch (err) {
      setMessage("❌ Failed to complete profile.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col relative overflow-hidden pb-10">
      {/* Decorative background elements */}
      <div className="absolute top-[-5%] left-[-5%] w-72 h-72 bg-orange-100 rounded-full blur-3xl opacity-40"></div>
      <div className="absolute bottom-[-5%] right-[-5%] w-80 h-80 bg-blue-50 rounded-full blur-3xl opacity-40"></div>

      {/* Header */}
      <div className="bg-white px-6 py-6 flex items-center shadow-sm border-b border-gray-100 relative z-20">
        <button
          onClick={() => navigate(-1)}
          disabled={loading}
          className={`p-2 -ml-2 hover:bg-gray-100 rounded-full transition text-gray-700 ${loading && "opacity-50"}`}
        >
          <IoMdArrowBack size={24} />
        </button>
        <div className="ml-4">
          <h1 className="text-xs font-black uppercase tracking-[0.2em] text-orange-600">Logistics Hub</h1>
          <p className="text-lg font-black text-gray-900 tracking-tighter">Rider Verification</p>
        </div>
      </div>

      <div className="flex-1 p-4 max-w-xl mx-auto w-full relative z-10">
        <form 
          onSubmit={handleSubmit} 
          className="bg-white rounded-[2.5rem] p-6 md:p-10 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-8"
        >
          {message && (
            <div className={`p-4 rounded-2xl text-[10px] font-black uppercase tracking-wider text-center ${message.includes('❌') ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-orange-50 text-orange-600 border border-orange-100'}`}>
              {message}
            </div>
          )}

          {/* Contact Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center text-orange-600">
                <FiPhone size={16} />
              </div>
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-900">Personal Details</h3>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Phone Number</label>
              <div className="relative">
                <FiPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="080 000 0000"
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 transition-all text-sm font-bold outline-none"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">LGA (Location)</label>
              <div className="relative">
                <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                <select
                  value={lga}
                  onChange={(e) => setLga(e.target.value)}
                  className="w-full pl-12 pr-10 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 transition-all text-sm font-bold appearance-none cursor-pointer outline-none"
                >
                  <option value="">Select LGA</option>
                  {lagosLGAs.map((item, index) => (
                    <option key={index} value={item}>{item}</option>
                  ))}
                </select>
                <FiChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 rotate-90 pointer-events-none" />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Residential Address</label>
              <div className="relative">
                <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Street, Building No, Landmark"
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 transition-all text-sm font-bold outline-none"
                />
              </div>
            </div>
          </div>

          {/* Document Uploads */}
          <div className="space-y-5 pt-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                <FiCamera size={16} />
              </div>
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-900">Verification Assets</h3>
            </div>

            {/* Selfie Upload */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">ID Verification (Selfie with NIN)</label>
              <label className={`flex items-center justify-between w-full px-5 py-5 border-2 border-dashed rounded-[1.5rem] cursor-pointer transition-all ${selfieFile ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-100 hover:border-orange-300'}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${selfieFile ? 'bg-blue-600 text-white' : 'bg-white text-gray-400 shadow-sm'}`}>
                    <FiCamera size={18} />
                  </div>
                  <span className={`text-[11px] font-bold uppercase tracking-tight ${selfieFile ? 'text-blue-700' : 'text-gray-500'}`}>
                    {selfieFile ? selfieFile.name : "Select Identity Image"}
                  </span>
                </div>
                {selfieFile && <FiCheckCircle className="text-blue-600" size={20} />}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setSelfieFile(e.target.files[0])}
                />
              </label>
            </div>

            {/* Logo/Avatar Upload */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Company Logo / Profile Picture</label>
              <label className={`flex items-center justify-between w-full px-5 py-5 border-2 border-dashed rounded-[1.5rem] cursor-pointer transition-all ${companyLogo ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-100 hover:border-orange-300'}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${companyLogo ? 'bg-green-600 text-white' : 'bg-white text-gray-400 shadow-sm'}`}>
                    <FiImage size={18} />
                  </div>
                  <span className={`text-[11px] font-bold uppercase tracking-tight ${companyLogo ? 'text-green-700' : 'text-gray-500'}`}>
                    {companyLogo ? companyLogo.name : "Upload Logo or Image"}
                  </span>
                </div>
                {companyLogo && <FiCheckCircle className="text-green-600" size={20} />}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setCompanyLogo(e.target.files[0])}
                />
              </label>
            </div>
          </div>

          {/* Submit Action */}
          <div className="pt-6">
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-5 rounded-2xl font-black text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-4 shadow-xl transition-all active:scale-95 ${
                loading ? "bg-orange-100 text-orange-400 cursor-not-allowed shadow-none" : "bg-gray-900 text-white hover:bg-black shadow-gray-200"
              }`}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                  Verifying Assets...
                </>
              ) : (
                <>
                  Submit Profile
                  <FiChevronRight size={18} />
                </>
              )}
            </button>
            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest text-center mt-6">
              By submitting, you agree to the <span className="text-gray-900 underline">Logistics Partner Terms</span>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteRiderPage;